/*
|--------------------------------------------------------------------------
| Utility: toastService.js
| Author: Sayed Ahmed
| Description: Centralized toast notification service using react-toastify.
|              Supports success, error, info, warning, promise states, and error lists.
|--------------------------------------------------------------------------
*/

import { toast } from "react-toastify";

// Default messages used across all toast types
const defaultMessages = {
  success: "Operation completed successfully",
  error: "Something went wrong",
  info: "Info",
  warning: "Warning",
  loading: "Processing...",
};

// Shared toast configuration options
const defaultOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

// Toast service methods
const toastService = {
  // Show success toast
  success: (msg = defaultMessages.success) =>
    toast.success(msg, defaultOptions),

  // Show error toast
  error: (msg = defaultMessages.error) =>
    toast.error(msg, defaultOptions),

  // Show info toast with custom icon
  info: (msg = defaultMessages.info) =>
    toast(msg, { ...defaultOptions, icon: "ℹ️" }),

  // Show warning toast with yellow background
  warning: (msg = defaultMessages.warning) =>
    toast(msg, {
      ...defaultOptions,
      icon: "⚠️",
      style: { background: "#facc15", color: "#000" },
    }),

  // Handle async operations with loading/success/error toasts
  promise: (promise, messages = {}) =>
    toast.promise(promise, {
      loading: messages.loading || defaultMessages.loading,
      success: {
        render: () => messages.success || defaultMessages.success,
        ...defaultOptions,
      },
      error: {
        render:
          typeof messages.error === "function"
            ? messages.error
            : () => messages.error || defaultMessages.error,
        ...defaultOptions,
      },
    }),

  // Display each validation error message from a list
  errorList: (errors) => {
    if (typeof errors === "object" && errors !== null) {
      Object.values(errors).forEach((errArr) => {
        if (Array.isArray(errArr)) {
          errArr.forEach((errMsg) =>
            toast.error(errMsg, defaultOptions)
          );
        } else {
          toast.error(errArr, defaultOptions);
        }
      });
    } else {
      toast.error(defaultMessages.error, defaultOptions);
    }
  },
};

export default toastService;
